import React, { useReducer, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import { space, site } from 'style/config';
import { Text, Divider } from 'components/ui';
import bazaarVoiceApi from 'services/api/thirdparty/BazaarVoice';
import RatingImageCard from 'components/ProductPage/ReviewsImageCarousel/RatingImageCard';
import LazyLoad from 'components/LazyLoad/LazyLoad';
import Actions from 'Actions';
import jsUtils from 'utils/javascript';
import localeUtils from 'utils/LanguageLocale';
import Carousel from 'components/Carousel';
import productPageBindings from 'analytics/bindingMethods/pages/productPage/productPageSOTBindings';
import { DebouncedResize } from 'constants/events';
import ReviewsImageCarouselEN from 'components/ProductPage/ReviewsImageCarousel/locales/ReviewsImageCarousel_en_US';
import ReviewsImageCarouselFR from 'components/ProductPage/ReviewsImageCarousel/locales/ReviewsImageCarousel_fr_CA';

const CARD_TRANSITION_OFFSET = space[1];
const RR_IMAGE_ANALYTICS_CAROUSEL_NAME = 'ratings&reviews image carousel';
const getText = localeUtils.getLocalisedDataFromResource(ReviewsImageCarouselEN, ReviewsImageCarouselFR);

const containerPaddedMatches = () => window.matchMedia(`(min-width: ${site.containerMax + space.container * 2}px)`).matches;

const isObservable = (index, pageCount) => {
    if (index !== 0 && index !== Math.floor(pageCount)) {
        return index % Math.floor(pageCount) === 0;
    }

    return false;
};

const initialState = {
    pageCount: null,
    isFlush: null,
    reviewsWithImage: [],
    reviewSelectedPhotoId: null,
    selectedReviewId: null,
    visibleItems: 0
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'INIT':
            return { ...state, pageCount: action.pageCount, visibleItems: action.visibleItems };
        case 'SET_FLUSH':
            return { ...state, isFlush: action.isFlush };
        case 'UPDATE_VISIBLE':
            return { ...state, visibleItems: state.visibleItems + Math.floor(action.pageCount) };
        case 'SET_MODAL_DATA':
            return { ...state, reviewSelectedPhotoId: action.reviewSelectedPhotoId, selectedReviewId: action.selectedReviewId };
        case 'SET_REVIEWS_DATA':
            return {
                ...state,
                reviewsWithImage: action.reviews,
                reviewSelectedPhotoId: action.reviewSelectedPhotoId,
                selectedReviewId: action.selectedReviewId
            };
        default:
            return state;
    }
};

const ReviewsImageCarousel = ({
    reviewImages, productId, productTitle, user, carouselContextId
}) => {
    const dispatch = useDispatch();
    const [state, stateDispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        const pageCount = 7;
        stateDispatch({ type: 'INIT', pageCount, visibleItems: pageCount * 2 });
        stateDispatch({ type: 'SET_FLUSH', isFlush: !containerPaddedMatches() });

        const handleResize = () => stateDispatch({ type: 'SET_FLUSH', isFlush: !containerPaddedMatches() });
        window.addEventListener(DebouncedResize, handleResize);

        return () => window.removeEventListener(DebouncedResize, handleResize);
    }, []);

    const openModal = (reviewsWithImage, reviewSelectedPhotoId, selectedReviewId) => {
        const reviewSelected = jsUtils.findInArray(reviewsWithImage, r => r.reviewId === selectedReviewId);
        dispatch(
            Actions.showReviewImageModal({
                isOpen: true,
                reviewSelected,
                reviewSelectedIndex: reviewImages.findIndex(r => r.reviewId === selectedReviewId),
                reviewsWithImage,
                reviewsReference: reviewImages,
                reviewUser: user,
                reviewProductTitle: productTitle,
                reviewSelectedPhotoId,
                isFromImageCarousel: true
            })
        );
    };

    const handleOnclick = (selectedReviewId, reviewSelectedPhotoId) => {
        productPageBindings.reviewImageClick({ selectedReviewId, productId });

        if (state.reviewsWithImage.length) {
            stateDispatch({ type: 'SET_MODAL_DATA', reviewSelectedPhotoId, selectedReviewId });
            openModal(state.reviewsWithImage, reviewSelectedPhotoId, selectedReviewId);
        } else {
            bazaarVoiceApi.getReviewsWithImage(productId).then(data => {
                if (data) {
                    stateDispatch({ type: 'SET_REVIEWS_DATA', reviews: data.results, reviewSelectedPhotoId, selectedReviewId });
                    openModal(data.results, reviewSelectedPhotoId, selectedReviewId);
                }
            });
        }
    };

    const imageItems = reviewImages.map((item, index) => (
        <RatingImageCard
            key={item.reviewId || index}
            imgSrc={item.thumbnailImageUrl}
            isObserved={isObservable(index, state.pageCount)}
            updateVisibleItems={() => stateDispatch({ type: 'UPDATE_VISIBLE', pageCount: state.pageCount })}
            reviewId={item.reviewId}
            isIncentivizedReview={item.isIncentivizedReview}
            onClickAction={() => handleOnclick(item.reviewId, item.photoId)}
        />
    ));

    return (
        <>
            <Divider
                marginY={[4, 5]}
                display={[null, null, 'none']}
            />
            {reviewImages.length > 0 && (
                <>
                    <Text
                        is='h3'
                        marginTop={[null, null, 7]}
                        fontSize='sm'
                        lineHeight='tight'
                        color='gray'
                        marginBottom={[2, 3]}
                        data-at={Sephora.debug.dataAt('image_carousel_title')}
                        children={getText('title')}
                    />
                    <div css={{ marginTop: -CARD_TRANSITION_OFFSET }}>
                        <LazyLoad
                            contextId={carouselContextId}
                            dataAt='image_carousel'
                            analyticsCarouselName={RR_IMAGE_ANALYTICS_CAROUSEL_NAME}
                            component={Carousel}
                            marginX={state.isFlush && '-container'}
                            scrollPadding={state.isFlush && 2}
                            gap={[1, 2]}
                            itemWidth={[136, 188]}
                            items={imageItems}
                            title={getText('title')}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default wrapFunctionalComponent(ReviewsImageCarousel, 'ReviewsImageCarousel');
